import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createBrowserDictationRecognizer } from "utilities/dictation/browserDictationRecognizer";
import type {
    IDictationRecognizer,
    IDictationSession,
} from "utilities/dictation/dictationRecognizer.types";

/**
 * Speech-to-text, as React state: the adapter between a recognizer's events and what a component
 * renders. Which engine answers sits behind {@link IDictationRecognizer}.
 */

const defaultRecognizer = createBrowserDictationRecognizer();

interface IUseDictationArgs {
    /** Called once a phrase is final -- the caller decides where the words land. */
    onPhrase: (text: string) => void;
    /** The engine to listen on. Defaults to the browser's; tests and future surfaces substitute. */
    recognizer?: IDictationRecognizer;
}

export function useDictation({ onPhrase, recognizer = defaultRecognizer }: IUseDictationArgs) {
    const [isListening, setIsListening] = useState(false);
    const [interimText, setInterimText] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Stopped, with the last phrase possibly still on its way. A send now would leave it behind.
    const [isSettling, setIsSettling] = useState(false);
    const sessionRef = useRef<IDictationSession | null>(null);
    const settlingRef = useRef(new Set<IDictationSession>());
    // Bumped by cancel. A session started before then may not deliver: its words were sent or
    // thrown away, and either way they no longer belong in the box. This also covers a phrase that
    // settles after the stop deadline, when the session has already left `settlingRef`.
    const generationRef = useRef(0);
    // A stopped session can end after unmount (see the unmount effect), when React 17 warns on any
    // state update.
    const isMountedRef = useRef(true);
    // The recognizer is handed one set of callbacks when it starts and keeps them for the session,
    // so the latest handler is kept in a ref rather than rebinding the session mid-phrase.
    const onPhraseRef = useRef(onPhrase);
    useEffect(() => {
        onPhraseRef.current = onPhrase;
    }, [onPhrase]);
    const recognizerRef = useRef(recognizer);
    useEffect(() => {
        recognizerRef.current = recognizer;
    }, [recognizer]);

    const isSupported = recognizer.isAvailable();

    const stop = useCallback(() => {
        const session = sessionRef.current;
        if (session) {
            // Marked before stop(): a recognizer may report its end from inside the call.
            settlingRef.current.add(session);
            setIsSettling(true);
            session.stop();
        }
        sessionRef.current = null;
        setIsListening(false);
        setInterimText("");
    }, []);

    /**
     * Ends the session and discards every word still being recognized, including the tail of one
     * already stopped. Call it when the draft is sent.
     */
    const cancel = useCallback(() => {
        generationRef.current++;
        sessionRef.current?.abort();
        sessionRef.current = null;
        settlingRef.current.forEach((session) => session.abort());
        settlingRef.current.clear();
        setIsSettling(false);
        setIsListening(false);
        setInterimText("");
    }, []);

    const start = useCallback(() => {
        if (sessionRef.current) {
            return;
        }

        setErrorMessage(null);

        // A recognizer that cannot start reports onEnd before start() returns, so the session it
        // hands back is already dead. Tracked explicitly because the ref reads null both for
        // "ended already" and "never held one".
        let hasEndedDuringStart = false;

        // A stopped session ends on the recognizer's clock, possibly after a new one has begun.
        // Its phrases still count -- they are the tail of what was said -- but its previews,
        // errors and end describe a session the builder has already left. Held in an object because
        // the callbacks can fire before `start()` has returned the session.
        const started: { session?: IDictationSession } = {};
        const isStale = () =>
            started.session !== undefined && sessionRef.current !== started.session;

        const generation = generationRef.current;

        started.session = recognizerRef.current.start({
            onPhrase: (text) => {
                if (generation === generationRef.current) {
                    onPhraseRef.current(text);
                }
            },
            onInterim: (text) => {
                if (!isStale()) {
                    setInterimText(text);
                }
            },
            onError: (error) => {
                if (isStale()) {
                    return;
                }
                setErrorMessage(
                    error === "mic-blocked"
                        ? "Microphone is blocked. Allow it in the browser to dictate."
                        : "Listening stopped unexpectedly.",
                );
            },
            onEnd: () => {
                // Before the stale check: settling is about a stopped session, which is stale by
                // definition once the builder has started another.
                if (
                    started.session &&
                    settlingRef.current.delete(started.session) &&
                    isMountedRef.current
                ) {
                    setIsSettling(settlingRef.current.size > 0);
                }
                if (isStale()) {
                    return;
                }
                hasEndedDuringStart = true;
                sessionRef.current = null;
                setInterimText("");
                setIsListening(false);
            },
        });

        if (!hasEndedDuringStart) {
            sessionRef.current = started.session;
            setIsListening(true);
        }
    }, []);

    // A stopped session is deliberately left to finish: the task assistant's draft outlives this
    // hook, so the builder's last phrase still reaches it after the panel closes.
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            sessionRef.current?.abort();
            sessionRef.current = null;
        };
    }, []);

    // Callers drive the recognizer from effects, so a fresh object each render would restart it.
    return useMemo(
        () => ({
            isSupported,
            isListening,
            isSettling,
            interimText,
            errorMessage,
            start,
            stop,
            cancel,
        }),
        [isSupported, isListening, isSettling, interimText, errorMessage, start, stop, cancel],
    );
}

/** Joins a dictated phrase onto the draft, spaced the way a typist would. */
export function appendDictation(draft: string, phrase: string): string {
    const spoken = phrase.trim();
    if (!spoken) {
        return draft;
    }

    const existing = draft.trimEnd();
    return existing ? `${existing} ${spoken}` : spoken;
}
